/* eslint-disable max-lines */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState } from "vuex";
import virtualList from 'vue3-virtual-scroll-list';
import dataMonitorMeasureListItem from "../../../../vue/individualWorkSupport/dataMonitor/DataMonitorMeasureListItem.vue";
import dmDidDataConversion from "../../../../../share/util/didDataConversion";
import logWrapper from "../../../../../share/logWrapper";
import dmUtil from "../../../dataMonitor/dataMonitorUtil";
import { shallowRef } from "vue";
import { UNION_GRAPH_MAX_LINES, SPLIT_GRAPH_MAX_LINES } from "./DataMonitorMeasure";
import { WS146_OTHER_CALCULATION_DID } from "./dmDataPointConversion";

const COMPONENT_NAME = "dataMonitorMeasureListWs146";

export default {
  name: COMPONENT_NAME,
  data() {
    return {
      items: shallowRef([]),
      preItems: [],
      columns: [],
      updateLength: {},
      itemComponent: shallowRef(dataMonitorMeasureListItem),
      isListScrolled: false,
      filterTextList: "",
      filterCheckList: [false, false],
      splitGraphLines: this.getSplitGraphLines(),
      splitGraphMaxLines: SPLIT_GRAPH_MAX_LINES,
      unionGraphLines: this.getUnionGraphLines(),
      unionGraphMaxLines: UNION_GRAPH_MAX_LINES,
      selectedRow: -1,
      // デバッグ環境か否か
      isDebug: process.env.VUE_APP_DEBUG_LOG && process.env.VUE_APP_DEBUG_LOG === "true",
      // デバッグ用のコンポーネントを表示するか否か
      showDebug: false,
      // HTMLにてログ出力するため定義
      logWrapper: logWrapper
    };
  },

  props: {
    // 項目選択データ
    selectedFunctionList: { type: Array, default: [], required: true },
    // 項目の単位、最大値、最小値、平均値
    funcValues: { type: Object, default: {}, required: false },
    // 計測中データ
    executionList: { type: Object, default: {}, required: true },
    // 再生データ
    measurementList: { type: Array, default: [], required: true },
    // 計測ID
    measuredId: { type: String, default: "", required: false },
    // グラフ対象
    selectedGraphCheckList: { type: Array, default: [], required: true },
    // ウィンドウの高さ
    windowSize: { type: String, default: window.innerWidth + '|' + window.innerHeight, required: false },
    // 変換情報リスト
    conversionList: { type: Object, default: {}, required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // 全画面モーダルの表示有無
    showModal: { type: Boolean, default: false, required: false },
    // 現在表示中のタブ
    currentTab: { type: Number, default: dmUtil.TAB.LIST, required: false },
    // 各グラフ項目の色
    splitGraphItemColors: { type: Object, default: {}, required: false },
    unionGraphItemColors: { type: Object, default: {}, required: false },
    // リスト表示するか否か
    isShow: { type: Boolean, default: true, required: false },
    // カーソル表示中か否か
    isCursorDisplay: { type: Boolean, default: false, required: false },
  },

  components: {
    "virtual-list": virtualList,
  },

  watch: {
    language: {
      handler() {
        // 言語選択時、ヘッダーを更新
        this.setColumns();
        this.setVirtualScrollListHeight();
      },
    },

    measuredId: {
      handler() {
        // 計測IDが変更された場合は表示を仕切り直し
        this.setDispItems(true);
      },
    },

    selectedFunctionList: {
      deep: true,
      handler() {
        // 選択項目が変更された場合は表示を仕切り直し
        this.setDispItems(true);
      },
    },

    executionList: {
      handler(val) {
        if (!val) {
          return;
        }

        const items = this.getMeasurementItems(val);
        if (items) {
          this.items = items;
          // filterをかける
          this.preItems = items;
          this.handleUpdateFilterCheck();
        }
      },
    },

    windowSize: {
      handler() {
        // ウィンドウサイズの変更時、仮想スクロール領域の高さを動的に設定
        this.setVirtualScrollListHeight();
      },
    },

    selectedGraphCheckList: {
      deep: true,
      handler() {
        // 項目変更された場合
        this.splitGraphLines = this.selectedGraphCheckList.filter((x) => x.checkValue[0]).length;
        this.unionGraphLines = this.selectedGraphCheckList.filter((x) => x.checkValue[1]).length;
        const items = this.getMeasurementItems({});
        // filterをかける
        this.preItems = items;
        this.handleUpdateFilterCheck();
      },
    },

    showModal: {
      handler() {
        this.$nextTick(() => {
          // ダイアログの描画完了後、リストの高さを再計算
          this.setVirtualScrollListHeight();
          // リストの再描画
          this.rerenderVirtualList();
        });
      }
    },

    currentTab: {
      handler() {
        this.setColumns();
      }
    },

    isShow: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            // リストを表示に切り替えた場合に、高さを再計算
            this.setVirtualScrollListHeight();
          });
        }
      }
    },

    isCursorDisplay: {
      handler(value) {
        if (value) {
          const checkedIndex = this.selectedGraphCheckList.findIndex(s => s.checkValue[1]);
          if (checkedIndex !== -1) {
            // カーソル設定時にチェックされている項目がすでにあれば、その項目をカーソル項目に設定
            this.selectedRow = checkedIndex;
            this.$emit("on-click-row", checkedIndex);
          }
        } else {
          this.selectedRow = -1;
        }
      }
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      individualMenus: state => state.vehicle.individualMenus,
      projectDetails: (state) => state.cache.projectDetails,
    }),

    emptyList() {
      return this.selectedFunctionList.map((e, index) => {
        // システム名取得
        const sysName = dmUtil.getSystem(e.data_monitor_id);

        return {
          data_monitor_id: e.data_monitor_id,
          splitCheck: false,
          unionCheck: false,
          line: 0,
          name: e.menu_title,
          value: null,
          unit: null,
          max: null,
          min: null,
          avg: null,
          index: index,
          system: dmUtil.getAbbrSysName(sysName)
        };
      });
    },
  },

  methods: {
    mountedLogic() {
      // リスト初期表示
      this.preItems = [];
      this.setColumns();
      this.setDispItems();

      // 仮想スクロール領域の高さを動的に設定 ※DOM生成後に実施
      this.$nextTick(() => {
        this.setVirtualScrollListHeight();
      });
    },

    /**
     * 仮想リストのID取得
     * @returns {string} 仮想リストのID
     */
    getVirtualListId() {
      return `${this.componentName()}-${this.dataMonitorFunctionSettings.getTypeCaller()}-virtual-list`;
    },

    /**
     * 分割グラフチェック数取得
     * @returns num
     */
    getSplitGraphLines() {
      let num = 0;
      if (this.selectedGraphCheckList) {
        num = this.selectedGraphCheckList.filter((x) => x.checkValue[0]).length;
      }
      return num;
    },

    /**
     * 結合グラフチェック数取得
     * @returns num
     */
    getUnionGraphLines() {
      let num = 0;
      if (this.selectedGraphCheckList) {
        num = this.selectedGraphCheckList.filter((x) => x.checkValue[1]).length;
      }
      return num;
    },

    /** 
     * 仮想スクロール領域の高さを動的に設定
     */
    setVirtualScrollListHeight() {
      const virtual_list_dev = this.$refs.virtual_list_dev; // 信号線選択画面では参照不可
      // リスト非表示中は高さ変更を行うと0になってしまうため、リスト表示中のみ行う
      if (virtual_list_dev && this.isShow) {
        // シークバー分だけ上にオフセットする形でサイズを再設定する、フィルタ分の調整を追加する
        const headerHeight = this.$refs.virtual_list_title.offsetHeight;
        const filterHeight = this.$refs.table_filter.offsetHeight;
        const height = virtual_list_dev.offsetHeight - headerHeight - filterHeight;
        // mount時はthis.$refsで参照不可。IDが被るため、呼び出し元画面のIDを付与。
        const virtual_list = document.querySelector(`#${this.getVirtualListId()}`);
        if (virtual_list) {
          // 仮想スクロールリストの高さ。グラフと横並び表示時は、カーソルアイコンと被らないようにリストの高さを少し小さくする
          virtual_list.style.height = this.currentTab === dmUtil.TAB.LIST ? height + 'px' : (height - 10) + 'px';

          // 仮想スクロールバーの有無により、ヘッダーのダミー部分の表示を切り替える
          let groupDiv = virtual_list.querySelector('div[role="group"]'); // for v2
          if (!groupDiv) {
            groupDiv = virtual_list.querySelector('.wrap'); // for v3
          }
          if (groupDiv) {
            this.isListScrolled = groupDiv.scrollHeight >= virtual_list.clientHeight;
          }
        }
      }
    },

    /**
     * 項目タイトル設定
     */
    setColumns() {
      const baseColumns = [
        {
          name: "split",
          label: "splitIconHeader",
          useHtmlBind: true,
        },
        {
          name: "union",
          label: "unionIconHeader",
          useHtmlBind: true,
        },
        {
          name: "name",
          label: this.$t("individual.header_data_monitor_table_list_item"),
          useHtmlBind: true,
        },
        {
          name: "value",
          label: this.$t("individual.header_data_monitor_table_value"),
          useHtmlBind: true,
        },
        {
          name: "unit",
          label: this.$t("individual.header_data_monitor_table_unit"),
          useHtmlBind: true,
        },
      ];

      const additionalFullColumns = [
        {
          name: "max",
          label: this.$t("individual.header_data_monitor_table_max"),
          useHtmlBind: true,
        },
        {
          name: "min",
          label: this.$t("individual.header_data_monitor_table_min"),
          useHtmlBind: true,
        },
        {
          name: "avg",
          label: this.$t("individual.header_data_monitor_table_avg"),
          useHtmlBind: true,
        },
      ];

      const displayColumns = this.currentTab !== dmUtil.TAB.LIST ? baseColumns : baseColumns.concat(additionalFullColumns);

      if (this.dataMonitorFunctionSettings.isSystemRow) {
        // リストにシステム列を3列目に追加
        displayColumns.splice(2, 0, {
          name: "system",
          label: this.currentTab !== dmUtil.TAB.LIST ? '' : this.$t("message.ecu_in_data_monitor_table_header"),
          useHtmlBind: true,
        });
      }

      this.columns = displayColumns;
    },

    /** 
     * 表示するリストを設定 
     * 
     * @param {boolean} init 初期化フラグ(初期化する場合はtrueを指定)
     */
    setDispItems(init = false) {
      if (!init) {
        // 再生データが存在する場合
        if (this.measurementList && this.measurementList.length) {
          const items = this.getPlayItems(this.currentTime);
          if (items) {
            this.items = items;
            return;
          }
        }

        // 計測中データが存在する場合
        if (this.executionList && Object.keys(this.executionList).length) {
          const items = this.getMeasurementItems(this.executionList);
          this.items = items;
          // filterをかける
          this.preItems = items;
          this.handleUpdateFilterCheck();
          return;
        }
      }

      // データがない場合
      this.items = this.emptyList;
    },

    /**
     * 表示用の計測データ作成
     */
    getMeasurementItems(executionList) {
      const items = [];
      let index = 0;

      for (const func of this.selectedFunctionList) {
        // 計測データ
        const conversionInfo = this.conversionList[func.data_monitor_id];
        const targetExecution = executionList[func.data_monitor_id];
        let item = {};
        const [systemName, itemId] = dmUtil.splitSystemAndItemId(func.data_monitor_id);

        if (targetExecution && dmUtil.VALUE_TRANSLATE_TYPE.includes(conversionInfo.data_type)) {
          let translated = dmUtil.getTranslateValue(
            this, itemId, targetExecution['convertedData'], conversionInfo, null, systemName
          );
          const did = dmUtil.getDid(dmUtil.getNoSystemItemId(func.data_monitor_id));
          // 2F01-2F08の場合はwsDidから文言を取得する
          if (WS146_OTHER_CALCULATION_DID.includes(did)) {
            const specialKey = `wsDid${systemName}.${itemId}_${targetExecution['convertedData']}`;
            translated = this.$te(`${specialKey}`) ? this.$t(`${specialKey}`) : translated;
          }
          if (conversionInfo.data_type === 'decExStr') {
            item = {
              ...targetExecution,
              convertedData: translated || dmDidDataConversion.convertValueDataForHex(targetExecution['convertedData'], conversionInfo)
            };
          } else {
            item = { ...targetExecution, convertedData: translated };
          }
        } else if (targetExecution && dmUtil.VALUE_CONVERT_DECIMAL_TYPE.includes(conversionInfo.data_type)) {
          const displayValue = dmDidDataConversion.convertDataToDisplayValue(targetExecution['convertedData'], conversionInfo);
          item = { ...targetExecution, convertedData: displayValue };
        } else {
          item = targetExecution;
        }

        // データセット
        items.push(this.itemsFormatter(func.data_monitor_id, item, func.menu_title, index, dmUtil.getAbbrSysName(systemName)));

        index++;
      }
      return items;
    },

    /** 
     * 取得データの整形処理 
     * @param {string} data_monitor_id DID
     * @param {object} item 計測データ
     * @param {string} name 項目名
     * @param {Number} index 行番号
     * @param {string} systemName システム名
     * @returns {object} 整形データ
     */
    itemsFormatter(data_monitor_id, item, name, index, systemName) {
      const format_data = {
        data_monitor_id: data_monitor_id,
        splitCheck: this.selectedGraphCheckList[index].checkValue[0],
        unionCheck: this.selectedGraphCheckList[index].checkValue[1],
        line: index,
        name: name,
        value: null,
        unit: null,
        max: null,
        min: null,
        avg: null,
        index: index,
        system: this.dataMonitorFunctionSettings.isSystemRow ? systemName : null
      };

      // 最大/最小/平均
      const funcVal = this.funcValues[data_monitor_id];

      if (funcVal) {
        const cache = new dmUtil.Cache();
        const cachedValue = cache.get(dmUtil.CACHE_CATEGORY.UNIT, funcVal.unit);
        // 0や空文字の場合はキャッシュを利用するため、nullやundefinedの時のみ翻訳情報を取得する
        if (cachedValue !== undefined && cachedValue !== null) {
          format_data.unit = cachedValue;
        } else {
          format_data.unit = this.$t(funcVal.unit);
          cache.set(dmUtil.CACHE_CATEGORY.UNIT, funcVal.unit, format_data.unit);
        }

        format_data.max = funcVal.maxDisp;
        format_data.min = funcVal.minDisp;
        format_data.avg = funcVal.avgDisp;
      }

      // 計測データあり
      if (item) {
        format_data.value = item.convertedData;
      }
      // 計測データ無し
      else {
        const filteredData = this.preItems.find(item => item.data_monitor_id === data_monitor_id);
        if (filteredData) {
          // 直前の値あり
          format_data.value = filteredData.value;
        }
      }

      return format_data;
    },

    /**
     * リストのセルのコンポーネントに渡すpropsを作成
     * @returns {object} リストのセルのコンポーネントに渡すprops
     */
    createListItemProps() {
      const refId = `${this.getVirtualListId()}-ref`;
      return {
        splitGraphLines: this.splitGraphLines,
        unionGraphLines: this.unionGraphLines,
        splitGraphMaxLines: this.splitGraphMaxLines,
        unionGraphMaxLines: this.unionGraphMaxLines,
        isSystemRow: this.dataMonitorFunctionSettings.isSystemRow,
        columnNames: this.columns.map(c => c.name),
        currentTab: this.currentTab,
        splitGraphItemColors: this.splitGraphItemColors,
        unionGraphItemColors: this.unionGraphItemColors,
        listRef: this.$refs[refId],
        selectedRow: this.selectedRow,
        onClickRowCallback: this.onClickRowCallback
      };
    },

    /**
     * タイトルCSS設定
     * @param {*} name 
     * @returns 
     */
    getTitleStyle(name) {
      // 項目
      if (name === 'name') {
        return 'title-name';
      }
      // 単位
      else if (name === 'unit') {
        return 'title-unit';
      }
      // 分割G/結合G
      else if (name === 'split' || name === 'union') {
        return 'title-check';
      }
      // システム
      else if (name === 'system') {
        return 'title-system';
      }
      // 値
      else {
        return 'title-value';
      }
    },

    /**
     * テキストフィルタ入力時の処理
     * @param {*} val フィルタ文字列
     */
    handleUpdateFilter(val) {
      const self = this;
      self.setFilteredItems(val);
      self.$nextTick(() => {
        // フィルタ後の高さを調整する
        self.setVirtualScrollListHeight();
      });
    },

    /**
     * チェックボックスフィルタ入力時の処理
     */
    handleUpdateFilterCheck() {
      this.handleUpdateFilter(this.filterTextList);
    },

    /**
     * フィルタ処理
     * @param {*} val 
     * @returns 
     */
    setFilteredItems(val) {
      const self = this;
      // チェックボックスでフィルタする
      const items = self.preItems.filter((x) => {
        if ((!self.filterCheckList[0]) && (!self.filterCheckList[1])) {
          return true;
        }
        if ((self.filterCheckList[0]) && (self.filterCheckList[1])) {
          return ((x.splitCheck) || (x.unionCheck));
        }
        if (self.filterCheckList[0]) {
          return (x.splitCheck);
        }
        if (self.filterCheckList[1]) {
          return (x.unionCheck);
        }
        return false;
      });

      if ((val !== self.filterTextList) || (self.filterTextList === undefined) ||
        (self.filterTextList === null) || (self.filterTextList === "")) {
        // フィルタ後のindexを振りなおす
        items.forEach((x, index) => {
          x.index = index;
        });
        self.items = items;
        return;
      }

      // テキストでフィルタする
      const filterdItems = items.filter((x) => x.name.toUpperCase().includes(self.filterTextList.toUpperCase()));
      // フィルタ後のindexを振りなおす
      filterdItems.forEach((x, index) => {
        x.index = index;
      });
      self.items = filterdItems;
    },

    /**
     * グラフ項目変更時処理
     * @param {*} type 分割G/結合G
     * @param {*} isSelect チェック有無
     * @param {*} index 一覧のindex
     */
    changeCheckValue(type, isSelect, index) {
      this.$emit("change-check-value", type, isSelect, index);

      if (type === dmUtil.GRAPH_CHECK_COLUMN.UNION && !isSelect && this.selectedRow === index) {
        const checkedIndex = this.selectedGraphCheckList.findIndex(s => s.checkValue[1]);
        // 外されたらカーソル選択を解除して、一番上を再選択
        this.selectedRow = checkedIndex;
        this.$emit("on-click-row", checkedIndex);
      } else if (type === dmUtil.GRAPH_CHECK_COLUMN.UNION && isSelect && this.selectedRow === -1 && this.isCursorDisplay) {
        // カーソル設定されていて、初めて結合グラフにチェックがされた場合、選択行をその行に設定
        this.selectedRow = index;
        this.$emit("on-click-row", index);
      }
    },

    /**
     * リスト行クリック時のコールバック処理
     * @param {number} rowNumber クリックされた行番号
     * @param {boolean} isUnionChecked クリックされた行の結合グラフがチェックされているか否か
     */
    onClickRowCallback(rowNumber, isUnionChecked) {
      if (this.isCursorDisplay && this.currentTab === dmUtil.TAB.UNION && isUnionChecked) {
        // 結合グラフタブの、チェックされた行がクリックされた場合
        this.selectedRow = rowNumber;
        this.$emit("on-click-row", rowNumber);
      }
    },

    /**
     * コンポーネント名を取得
     * @returns コンポーネント名
     */
    componentName() {
      return this.$options.name;
    },

    /**
     * 仮想リストの再描画を行う
     */
    rerenderVirtualList() {
      const refId = `${this.getVirtualListId()}-ref`;
      this.setVirtualScrollListHeight();
      // リストの再描画のため、ごくわずかに表示を下げてもどす
      this.$refs[refId].scrollToOffset(1);
      this.$nextTick(() => {
        this.$refs[refId].scrollToOffset(0);
      });
    },

    /**
     * TSVファイルダウンロード処理(DEBUG用)
     */
    downloadTSV() {
      const filtered = this.items.map(item => {
        // systemが不要で取り除くため、次行のみESLintを抑制
        // eslint-disable-next-line no-unused-vars
        const { system, ...other } = item;
        return other;
      });
      dmUtil.tsvFileExport(filtered, 'data_monitor_list.tsv');
    },

    /**
     * デバッグ用のコンポーネントを表示する(DEBUG用)
     */
    showDebugButton() {
      if (this.isDebug) {
        this.showDebug = true;
      }
    }
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },

};
